import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkle, Calculator, FilePdf, MagnifyingGlass, ForkKnife, Clock, ChefHat,
  ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api } from "../lib/kostra";
import { useAuth } from "../lib/auth";

export default function InspirationPage() {
  const [items, setItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [section, setSection] = useState("all");
  const navigate = useNavigate();
  const { guard } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/chef-recipes");
        setItems(data.items || []);
        setSections(data.sections || []);
      } catch { toast.error("Failed to load inspiration"); }
      finally { setLoading(false); }
    })();
  }, []);

  const grouped = useMemo(() => {
    const filtered = items.filter((r) => {
      if (section !== "all" && r.section !== section) return false;
      if (!q) return true;
      const t = q.toLowerCase();
      return (
        (r.dish_name || "").toLowerCase().includes(t) ||
        (r.section || "").toLowerCase().includes(t) ||
        (r.chef || "").toLowerCase().includes(t)
      );
    });
    const out = {};
    filtered.forEach((r) => {
      out[r.section] = out[r.section] || [];
      out[r.section].push(r);
    });
    return out;
  }, [items, q, section]);

  const downloadPdf = guard(async (slug, name) => {
    try {
      const { data } = await api.get(`/chef-recipes/${slug}/export/pdf`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `KOSTRA_${(name || "recipe").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("PDF download failed"); }
  }, "download exports");

  const costRecipe = guard((slug) => {
    navigate(`/?from=chef:${slug}`);
  }, "cost a recipe");

  const sectionsToRender = section === "all" ? sections : [section];

  return (
    <div className="app-shell">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8 pb-10">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="max-w-2xl">
              <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-medium inline-flex items-center gap-2">
                <Sparkle size={14} weight="fill" /> Inspiration Library
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-medium mt-2 leading-tight">
                Chef-curated recipes, <br className="hidden md:block" />
                <span className="text-[#D4AF37]">priced in one click.</span>
              </h1>
              <p className="text-white/75 text-sm md:text-base mt-3">
                Browse a curated library inspired by India's leading chefs. Tap <strong className="text-white">Cost This Recipe</strong> to auto-load every ingredient into the calculator — ready to fine-tune, save and export.
              </p>
            </div>
            <div className="flex items-center gap-2 w-full md:w-80 mt-4">
              <div className="relative flex-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search dish, chef or category..."
                  className="pl-9 h-11 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#D4AF37]"
                  data-testid="inspiration-search"
                />
              </div>
            </div>
          </div>

          {/* Section chips */}
          <div className="mt-6 flex flex-wrap gap-2" data-testid="section-chips">
            <button
              onClick={() => setSection("all")}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition border ${
                section === "all"
                  ? "bg-[#D4AF37] text-[#0A3B24] border-[#D4AF37]"
                  : "bg-white/5 text-white/85 border-white/20 hover:bg-white/10"
              }`}
              data-testid="chip-all"
            >
              All categories
            </button>
            {sections.map((s) => (
              <button
                key={s}
                onClick={() => setSection(s)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition border ${
                  section === s
                    ? "bg-[#D4AF37] text-[#0A3B24] border-[#D4AF37]"
                    : "bg-white/5 text-white/85 border-white/20 hover:bg-white/10"
                }`}
                data-testid={`chip-${s.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10 space-y-12">
        {loading ? (
          <div className="kostra-card p-12 text-center text-sm text-[#4B5563]">Loading inspiration...</div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="kostra-card p-12 text-center text-sm text-[#4B5563]" data-testid="inspiration-empty">
            No recipes match your search.
          </div>
        ) : (
          sectionsToRender.map((sec) => {
            const list = grouped[sec];
            if (!list || list.length === 0) return null;
            return (
              <section key={sec} data-testid={`section-${sec.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="flex items-end justify-between mb-5">
                  <div>
                    <div className="kostra-data-label">Category</div>
                    <h2 className="font-display text-2xl md:text-3xl text-[#0A3B24] font-semibold mt-1">{sec}</h2>
                  </div>
                  <span className="text-xs text-[#4B5563]">{list.length} recipe{list.length > 1 ? "s" : ""}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {list.map((r, i) => (
                    <motion.article
                      key={r.slug}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.32, delay: i * 0.05 }}
                      className="kostra-card overflow-hidden flex flex-col group"
                      data-testid={`chef-card-${r.slug}`}
                    >
                      <div className="h-44 bg-[#F9FAFB] relative overflow-hidden">
                        {r.image ? (
                          <img
                            src={r.image}
                            alt={r.dish_name}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-[#4B5563] text-xs">No photo</div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent" />
                        <span className="absolute top-3 left-3 gold-pill text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold">
                          {r.category}
                        </span>
                        {r.chef && (
                          <span className="absolute bottom-3 left-3 text-[11px] text-white/95 font-medium inline-flex items-center gap-1.5 bg-black/35 backdrop-blur-sm rounded-full px-2.5 py-1">
                            <ChefHat size={12} weight="fill" /> {r.chef}
                          </span>
                        )}
                      </div>
                      <div className="p-5 flex-1 flex flex-col">
                        <h3 className="font-display text-lg text-[#0A3B24] font-semibold tracking-tight">{r.dish_name}</h3>
                        {r.notes && (
                          <p className="text-xs text-[#4B5563] mt-1.5 line-clamp-2">{r.notes}</p>
                        )}
                        <div className="grid grid-cols-3 gap-3 mt-4 text-[11px]">
                          <div className="flex items-center gap-1 text-[#4B5563]">
                            <ForkKnife size={12} weight="duotone" />
                            <span><strong className="text-[#111827] font-semibold">{r.ingredient_count}</strong> ingr</span>
                          </div>
                          <div className="flex items-center gap-1 text-[#4B5563]">
                            <Clock size={12} weight="duotone" />
                            <span><strong className="text-[#111827] font-semibold">{r.step_count}</strong> steps</span>
                          </div>
                          <div className="flex items-center gap-1 text-[#4B5563]">
                            <span><strong className="text-[#111827] font-semibold">{r.num_portions}</strong> portion{r.num_portions > 1 ? "s" : ""}</span>
                          </div>
                        </div>
                        <div className="mt-5 pt-4 border-t border-[#E5E7EB] space-y-2 mt-auto">
                          <Button
                            onClick={() => costRecipe(r.slug)}
                            className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold w-full h-11"
                            data-testid={`cost-${r.slug}`}
                          >
                            <Calculator size={16} weight="bold" className="mr-2" /> Cost This Recipe
                            <ArrowRight size={16} weight="bold" className="ml-2" />
                          </Button>
                          <Button
                            onClick={() => downloadPdf(r.slug, r.dish_name)}
                            variant="outline"
                            className="w-full h-10 border-[#D4AF37]/60 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold"
                            data-testid={`download-${r.slug}`}
                          >
                            <FilePdf size={14} weight="bold" className="mr-2" /> Download Recipe PDF
                          </Button>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </div>
              </section>
            );
          })
        )}
      </main>

      <Footer />
    </div>
  );
}
